import { SemboDate } from '@libs/sembo-dates';
import type { MonthIndex } from '@libs/sembo-dates';

export interface PreferredMonthSelectionState {
  selectedMonthDate: Date;
  preferredMonth: MonthIndex | undefined;
  startSearchDate: Date;
  endSearchDate: Date;
}

export function getCommittedPreferredMonthState(
  selectedDate: Date,
  currentDate: Date
): PreferredMonthSelectionState {
  const selectedMonthDate = new SemboDate(selectedDate).startOf('month').get();

  return {
    selectedMonthDate,
    preferredMonth: selectedMonthDate.getMonth() as MonthIndex,
    startSearchDate: getStartSearchDateFromPreferredDay(
      selectedMonthDate,
      currentDate
    ),
    endSearchDate: getEndSearchDateFromPreferredDay(selectedMonthDate),
  };
}

export function getStartSearchDateFromPreferredDay(
  preferredDay: Date,
  currentDate: Date
): Date {
  const startOfPreferredDayMonth = new SemboDate(preferredDay)
    .startOf('month')
    .get();

  const todayDate = new SemboDate(currentDate).startOf('day').get();

  if (new SemboDate(startOfPreferredDayMonth).isBefore(todayDate)) {
    return todayDate;
  }

  return startOfPreferredDayMonth;
}

export function getStartSearchDateWithoutPreferredDay(
  preferredMonth: MonthIndex | undefined
): Date | undefined {
  if (preferredMonth !== undefined) {
    const preferredMonthDate = new Date();
    preferredMonthDate.setMonth(preferredMonth, 1);

    if (
      new SemboDate(preferredMonthDate).isBefore(new Date()) &&
      new Date().getMonth() === preferredMonth
    ) {
      preferredMonthDate.setDate(new Date().getDate());
    }

    const preferredMonthSemboDate = new SemboDate(preferredMonthDate);

    if (preferredMonthSemboDate.endOf('month').isBefore(new Date())) {
      preferredMonthSemboDate.add({ years: 1 });
    }
    return preferredMonthSemboDate.get();
  }
}

export function getEndSearchDateFromPreferredDay(preferredDay: Date): Date {
  return new SemboDate(preferredDay).endOf('month').get();
}

export function getPreferredMonthSelectionState(
  selectedDate: Date,
  expandedMonth: Date | undefined,
  currentDate: Date
): PreferredMonthSelectionState {
  const committedState = getCommittedPreferredMonthState(
    selectedDate,
    currentDate
  );
  const isSelectedMonthExpanded =
    expandedMonth &&
    new SemboDate(expandedMonth).isSameMonth(committedState.selectedMonthDate);

  return {
    ...committedState,
    preferredMonth: isSelectedMonthExpanded
      ? undefined
      : committedState.preferredMonth,
  };
}

export function getEndSearchDateWithoutPreferredDay(
  preferredMonth: MonthIndex | undefined
): Date | undefined {
  const startDate = getStartSearchDateWithoutPreferredDay(preferredMonth);

  if (startDate) {
    const searchingMonthStartDate = new SemboDate(startDate);

    return searchingMonthStartDate.endOf('month').get();
  }
}
